import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Loader2, CheckCircle2, AlertTriangle, FileText, ChevronRight, Search, X,
  Clock, ArrowRight, ScanLine,
} from "lucide-react";
import { toast } from "sonner";
import { getSplitBatch, resolveSplitSegment, searchPatients } from "@/lib/noteClaim";
import { describeError } from "@/lib/api";

const INK = "#141412", HAIR = "#E9E9EC", GREY = "#6E6E6B", SUB = "#6C6C69", INDIGO = "#5856D6";

const STATUS_PILL = {
  processing: { label: "Processing", cls: "bg-[#ECECFB] text-[#5856D6]", Icon: Loader2, spin: true },
  done: { label: "Done", cls: "bg-[#F1F1F4] text-[#6C6C69]", Icon: CheckCircle2 },
  failed: { label: "Failed", cls: "bg-[#FBE9E7] text-[#C22219]", Icon: AlertTriangle },
};

function Pill({ status }) {
  const s = STATUS_PILL[status] || STATUS_PILL.processing;
  const I = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10.5px] font-bold px-2.5 py-1 rounded-full ${s.cls}`} data-testid="split-tracker-status">
      <I size={11} className={s.spin ? "animate-spin" : ""} />{s.label}
    </span>
  );
}

// Inline resolver for a failed segment: find a roster patient (MRN) + pick a DOS → create a visit.
function ResolveForm({ clientId, seg, onResolve, onCancel, busy }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null); // { mrn, name, dob }
  const [dos, setDos] = useState(seg?.dos || "");
  const tRef = useRef(null);

  useEffect(() => {
    if (!q || q.trim().length < 2) { setResults([]); return undefined; }
    setSearching(true);
    clearTimeout(tRef.current);
    tRef.current = setTimeout(async () => {
      try {
        const rows = await searchPatients(clientId, q.trim());
        setResults((rows || []).slice(0, 8));
      } catch { setResults([]); } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(tRef.current);
  }, [q, clientId]);

  const nameOf = (r) => r.name || [r.first_name, r.last_name].filter(Boolean).join(" ").trim() || r.mrn;
  const canSubmit = !!picked?.mrn && !!dos && !busy;

  return (
    <div className="mt-2 rounded-[12px] border bg-white p-3" style={{ borderColor: HAIR }} data-testid={`split-resolve-${seg.index}`}>
      {!picked ? (
        <>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: GREY }} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a roster patient by name or MRN…"
              className="w-full text-[12.5px] border rounded-[9px] pl-8 pr-3 py-2 outline-none" style={{ borderColor: HAIR }}
              data-testid={`split-resolve-search-${seg.index}`} />
          </div>
          <div className="mt-2 max-h-[180px] overflow-y-auto">
            {searching ? (
              <div className="text-[12px] py-2 inline-flex items-center gap-1.5" style={{ color: GREY }}><Loader2 size={12} className="animate-spin" />Searching…</div>
            ) : results.length ? results.map((r) => (
              <button key={r.mrn} onClick={() => { setPicked({ mrn: r.mrn, name: nameOf(r), dob: r.dob || r.date_of_birth || "" }); setDos((d) => d || seg?.dos || ""); }}
                className="w-full flex items-center gap-2 text-left px-2 py-2 rounded-[8px] hover:bg-black/[.04]" data-testid={`split-resolve-result-${r.mrn}`}>
                <span className="text-[12.5px] font-semibold flex-1 min-w-0 truncate" style={{ color: INK }}>{nameOf(r)}</span>
                <span className="text-[11px]" style={{ color: GREY }}>{r.dob || r.date_of_birth || ""}</span>
                <span className="text-[11px] font-mono" style={{ color: SUB }}>{r.mrn}</span>
              </button>
            )) : q.trim().length >= 2 ? (
              <div className="text-[12px] py-2" style={{ color: GREY }}>No roster match. Add the patient in the roster first, then resolve with the new MRN.</div>
            ) : (
              <div className="text-[12px] py-2" style={{ color: GREY }}>Type at least 2 characters.</div>
            )}
          </div>
        </>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[12.5px] font-semibold" style={{ color: INK }}>{picked.name}</span>
          <span className="text-[11px] font-mono" style={{ color: SUB }}>{picked.mrn}</span>
          <button onClick={() => setPicked(null)} className="text-[11.5px] font-semibold" style={{ color: INDIGO }} data-testid={`split-resolve-change-${seg.index}`}>Change</button>
          <span className="flex-1" />
          <label className="text-[11px]" style={{ color: GREY }}>DOS</label>
          <input type="date" value={dos} onChange={(e) => setDos(e.target.value)} className="text-[12px] border rounded-[8px] px-2 py-1.5" style={{ borderColor: HAIR }} data-testid={`split-resolve-dos-${seg.index}`} />
        </div>
      )}
      <div className="flex items-center justify-end gap-2 mt-3">
        <button onClick={onCancel} className="text-[12px] font-semibold px-3 py-1.5" style={{ color: GREY }} data-testid={`split-resolve-cancel-${seg.index}`}>Cancel</button>
        <button onClick={() => onResolve(seg.index, { mrn: picked.mrn, dos })} disabled={!canSubmit}
          className="text-[12px] font-bold text-white rounded-[8px] px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: INK }}
          data-testid={`split-resolve-submit-${seg.index}`}>{busy ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}Create visit</button>
      </div>
    </div>
  );
}

function Segment({ seg, onOpenVisit, onResolve, resolving, setResolving, busy, clientId, onFind }) {
  const created = seg.status === "created";
  return (
    <div className="px-4 py-3" data-testid={`split-seg-${seg.index}`}>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex-none">{created ? <CheckCircle2 size={16} className="text-[#5856D6]" /> : <AlertTriangle size={16} className="text-[#C22219]" />}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold" style={{ color: INK }}>{seg.patient_label || "Unknown patient"}</span>
            {seg.dos ? <span className="text-[11.5px]" style={{ color: GREY }}>· {seg.dos}</span> : <span className="text-[11.5px]" style={{ color: "#C22219" }}>· no DOS</span>}
            {(seg.pages || []).length ? <span className="text-[11px]" style={{ color: GREY }}>· p{(seg.pages || []).join(", ")}</span> : null}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
            {(seg.types || []).map((t, i) => (
              <span key={i} className="text-[10.5px] font-semibold rounded-[6px] px-1.5 py-0.5" style={{ background: "#F1F1F4", color: SUB }}>{String(t).replace(/_/g, " ")}</span>
            ))}
          </div>
          {!created && seg.reason ? <div className="text-[11.5px] mt-1.5 leading-snug" style={{ color: "#B06F00" }}>{seg.reason}</div> : null}
        </div>
        {created && seg.note_id ? (
          <button onClick={() => onOpenVisit(seg.note_id)} className="text-[12px] font-semibold inline-flex items-center gap-1 flex-none" style={{ color: INDIGO }} data-testid={`split-seg-open-${seg.index}`}>Open visit <ChevronRight size={13} /></button>
        ) : !created && resolving !== seg.index ? (
          <div className="flex items-center gap-3 flex-none">
            {onFind && seg.patient_label ? <button onClick={() => onFind(seg)} className="text-[12px] font-semibold" style={{ color: INDIGO }} data-testid={`split-seg-find-${seg.index}`}>Find in Encounter review</button> : null}
            <button onClick={() => setResolving(seg.index)} className="text-[12px] font-bold text-white rounded-[8px] px-3 py-1.5" style={{ background: INK }} data-testid={`split-seg-resolve-${seg.index}`}>Resolve</button>
          </div>
        ) : null}
      </div>
      {!created && resolving === seg.index ? (
        <div className="pl-7">
          <ResolveForm clientId={clientId} seg={seg} busy={busy} onResolve={onResolve} onCancel={() => setResolving(null)} />
        </div>
      ) : null}
    </div>
  );
}

// Live tracker for a single smart-upload batch. Polls every 3s until done/failed and lets
// staff resolve failed segments (the error queue) from here — no re-upload.
export default function SplitBatchTracker({ batchId, clientId, onOpenVisit, onDone, embedded = false, onFind }) {
  const [batch, setBatch] = useState(null);
  const [err, setErr] = useState(null);
  const [resolving, setResolving] = useState(null);
  const [busy, setBusy] = useState(false);
  const aliveRef = useRef(true);

  const fetchBatch = useCallback(async () => {
    try { const b = await getSplitBatch(batchId); if (aliveRef.current) { setBatch(b); setErr(null); } return b; }
    catch (e) { if (aliveRef.current) setErr(describeError(e)); return null; }
  }, [batchId]);

  useEffect(() => {
    aliveRef.current = true;
    fetchBatch();
    const iv = setInterval(async () => {
      if (document.hidden) return;
      const b = await fetchBatch();
      if (b && b.status !== "processing") clearInterval(iv);
    }, 3000);
    return () => { aliveRef.current = false; clearInterval(iv); };
  }, [fetchBatch]);

  const onResolve = async (segIndex, body) => {
    setBusy(true);
    try {
      await resolveSplitSegment(batchId, segIndex, body);
      toast.success("Visit created from the error queue");
      setResolving(null);
      await fetchBatch();
    } catch (e) { toast.error(describeError(e)); } finally { setBusy(false); }
  };

  const segments = (batch?.segments || []).map((s, i) => ({ ...s, index: i }));
  const created = segments.filter((s) => s.status === "created");
  const failed = segments.filter((s) => s.status !== "created");

  return (
    <div className={embedded ? "" : "px-5 py-4"} data-testid="split-tracker">
      <div className="flex items-start gap-3 mb-3">
        <span className="w-9 h-9 rounded-[11px] grid place-items-center flex-none" style={{ background: "#ECECFB", color: INDIGO }}><ScanLine size={18} /></span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[14px] font-bold truncate" style={{ color: INK }}>{batch?.filename || "Multi-part upload"}</span>
            {batch ? <Pill status={batch.status} /> : null}
          </div>
          <div className="text-[12px] mt-0.5" style={{ color: GREY }} data-testid="split-tracker-summary">
            {batch ? (
              <>
                {batch.num_pages != null ? `${batch.num_pages} page${batch.num_pages === 1 ? "" : "s"} · ` : ""}
                <b style={{ color: "#1F9D57" }}>{batch.created_count ?? created.length}</b> visit{(batch.created_count ?? created.length) === 1 ? "" : "s"} created
                {(batch.failed_count ?? failed.length) > 0 ? <> · <b style={{ color: "#C22219" }}>{batch.failed_count ?? failed.length}</b> in the error queue</> : null}
              </>
            ) : err ? <span style={{ color: "#C22219" }}>{err}</span> : "Reading & splitting your documents…"}
          </div>
        </div>
      </div>

      {batch?.status === "processing" ? (
        <div className="flex items-center gap-2 text-[12.5px] rounded-[10px] px-3 py-2.5 mb-3" style={{ background: "#F7F7FB", color: SUB }} data-testid="split-tracker-working">
          <Clock size={14} /> Splitting by patient &amp; date of service — this updates live and keeps running if you close this.
        </div>
      ) : null}

      {segments.length ? (
        <div className="rounded-[14px] border overflow-hidden bg-white" style={{ borderColor: HAIR }}>
          {created.length ? (
            <>
              <div className="px-4 py-2 text-[10.5px] font-bold uppercase" style={{ color: GREY, letterSpacing: ".05em", background: "#FAFAFB", borderBottom: `1px solid ${HAIR}` }}>Visits created</div>
              <div className="divide-y" style={{ borderColor: HAIR }}>
                {created.map((seg) => <Segment key={seg.index} seg={seg} clientId={clientId} onOpenVisit={onOpenVisit} onResolve={onResolve} resolving={resolving} setResolving={setResolving} busy={busy} onFind={onFind} />)}
              </div>
            </>
          ) : null}
          {failed.length ? (
            <>
              <div className="px-4 py-2 text-[10.5px] font-bold uppercase inline-flex w-full items-center gap-1.5" style={{ color: "#B06F00", letterSpacing: ".05em", background: "#FFF8EE", borderTop: created.length ? `1px solid ${HAIR}` : undefined, borderBottom: `1px solid ${HAIR}` }} data-testid="split-tracker-errorqueue">To review — error queue ({failed.length})</div>
              <div className="divide-y" style={{ borderColor: HAIR }}>
                {failed.map((seg) => <Segment key={seg.index} seg={seg} clientId={clientId} onOpenVisit={onOpenVisit} onResolve={onResolve} resolving={resolving} setResolving={setResolving} busy={busy} onFind={onFind} />)}
              </div>
            </>
          ) : null}
        </div>
      ) : !err && batch?.status !== "processing" ? (
        <div className="text-[12.5px] py-6 text-center" style={{ color: GREY }} data-testid="split-tracker-empty">No visits were produced from this upload.</div>
      ) : null}

      {!embedded ? (
        <div className="flex items-center justify-end mt-4">
          <button onClick={onDone} className="text-[12.5px] font-bold text-white rounded-[9px] px-4 py-2" style={{ background: INK }} data-testid="split-tracker-done">
            {batch?.status === "processing" ? "Leave — runs in the background" : "Done"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
